import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FiX, 
  FiHome, 
  FiFileText, 
  FiCreditCard, 
  FiUsers, 
  FiPackage, 
  FiBarChart, 
  FiSettings,
  FiUser
} from 'react-icons/fi';
import styles from './Sidebar.module.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
}

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: <FiHome size={20} />,
    roles: ['admin', 'accountant', 'sales', 'client']
  },
  {
    label: 'Invoices',
    path: '/invoices',
    icon: <FiFileText size={20} />,
    roles: ['admin', 'accountant', 'sales']
  },
  {
    label: 'Quotes',
    path: '/quotes',
    icon: <FiCreditCard size={20} />,
    roles: ['admin', 'accountant', 'sales']
  },
  {
    label: 'Clients',
    path: '/clients',
    icon: <FiUsers size={20} />,
    roles: ['admin', 'accountant', 'sales']
  },
  {
    label: 'Products',
    path: '/products',
    icon: <FiPackage size={20} />,
    roles: ['admin', 'accountant', 'sales']
  },
  {
    label: 'Reports',
    path: '/reports',
    icon: <FiBarChart size={20} />,
    roles: ['admin', 'accountant']
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: <FiSettings size={20} />,
    roles: ['admin', 'accountant', 'sales', 'client']
  }
];

export function Sidebar({ isOpen, onClose, isCollapsed }: SidebarProps) {
  const { user } = useAuth();

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role || 'client')
  );

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className={styles.overlay} onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''} ${isCollapsed ? styles.collapsed : ''}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>C-Band</h2>
          {!isCollapsed && (
            <button 
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close menu"
            >
              <FiX size={24} />
            </button>
          )}
        </div>

        <nav className={styles.nav}>
          <ul className={styles.menuList}>
            {filteredMenuItems.map((item) => (
              <li key={item.path} className={styles.menuItem}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => 
                    `${styles.menuLink} ${isActive ? styles.active : ''}`
                  }
                  onClick={onClose}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className={styles.icon}>{item.icon}</span>
                  {!isCollapsed && <span className={styles.label}>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.footer}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt={user.firstName} />
              ) : (
                <FiUser size={16} />
              )}
            </div>
            {!isCollapsed && (
              <div className={styles.userDetails}>
                <p className={styles.userName}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className={styles.userRole}>
                  {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
